// App.jsx

import { useState, useEffect } from "react";
import { Routes, Route, useNavigate, NavLink } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import cognitoService from "./services/cognitoService";
import GenerateActivityForm from "./components/GenerateActivityForm.jsx";
import HWHistory from "./components/HWHistory.jsx";
import Welcome from "./components/Welcome.jsx";
import Feedback from "./components/Feedback.jsx";
import Chat from "./components/Chat.jsx";
import AgeVerification from "./components/AgeVerification.jsx";
import AdminDashboard from "./components/AdminDashboard.jsx";
import { TransparencyPage } from "./components/Transparency.jsx";
import "./styles/App.css";

function App() {
  const { isAuthenticated, loading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [processingCallback, setProcessingCallback] = useState(false);
  const [ageVerified, setAgeVerified] = useState(() => {
    // Check if age was previously verified in this session
    return sessionStorage.getItem('ageVerified') === 'true';
  });
  const [showAgeVerification, setShowAgeVerification] = useState(false);

  // Handle Cognito callback on component mount
  useEffect(() => {
    const handleCognitoCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const error = urlParams.get('error');
      const state = urlParams.get('state');

      if (import.meta.env.DEV) {
        console.log('Callback URL params:', {
          path: window.location.pathname,
          hasCode: !!code,
          error: error,
          state: state,
          isAuthenticated: isAuthenticated
        });
      }

      if (error) {
        console.error('Cognito authentication error:', error);
        alert(`Login failed: ${error}`);
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }

      if (code && !isAuthenticated) {
        // Check if we've already processed this code
        const lastProcessedCode = sessionStorage.getItem('lastProcessedCode');
        if (lastProcessedCode === code) {
          window.history.replaceState({}, document.title, window.location.pathname);
          navigate('/history', { replace: true });
          return;
        }

        setProcessingCallback(true);
        try {
          // Use cognitoService for token exchange
          await cognitoService.exchangeCodeForTokens(code);
          
          // Mark this code as processed
          sessionStorage.setItem('lastProcessedCode', code);
          
          // Refresh user in AuthContext
          const refreshResult = await refreshUser();
          if (import.meta.env.DEV && !refreshResult) {
            console.warn('RefreshUser returned false, but continuing...');
          }
          
          // Navigate to history page after successful authentication
          navigate('/history', { replace: true });
        } catch (error) {
          console.error('Error processing callback:', error);
          alert(`Login processing failed. Please try again. Error: ${error.message}`);
          // Clean up URL on error
          window.history.replaceState({}, document.title, window.location.pathname);
        } finally {
          setProcessingCallback(false);
        }
      } else if (code && isAuthenticated) {
        window.history.replaceState({}, document.title, window.location.pathname);
        navigate('/history', { replace: true });
      }
    };

    handleCognitoCallback();
  }, [isAuthenticated]);

  // Age verification handlers
  const handleAgeVerified = (data) => {
    setAgeVerified(true);
    sessionStorage.setItem('ageVerified', 'true');
    setShowAgeVerification(false);
    
    // Now proceed with the login after age verification
    proceedWithLogin();
  };

  const handleAgeRejected = (data) => {
    alert(
      'You must be at least 18 years old to use this service. ' +
      'If you are under 18, please have a parent or guardian create an account on your behalf.'
    );
    // Clear any stored age verification
    sessionStorage.removeItem('ageVerified');
    setAgeVerified(false);
    setShowAgeVerification(false);
  };

  // Function to proceed with actual login to Cognito
  const proceedWithLogin = () => {
    try {
      // Use cognitoService to build login URL
      const loginUrl = cognitoService.buildLoginUrl();
      window.location.href = loginUrl;
    } catch (error) {
      console.error('Error building login URL:', error);
      alert(`Login configuration error: ${error.message}`);
    }
  };

  // Login function to be passed to Welcome component
  const handleLogin = () => {
    // Check if age is already verified
    if (ageVerified) {
      // Age already verified, proceed directly to login
      proceedWithLogin();
    } else {
      // Show age verification first
      setShowAgeVerification(true);
    }
  };

  // Show loading state while processing callback or auth is loading
  if (loading || processingCallback) {
    return (
      <div className="app-container">
        <div className="loading-container">
          <p>{processingCallback ? 'Processing login...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  // If user is not authenticated, show unauthenticated routes
  if (!isAuthenticated) {
    return (
      <>
        <Routes>
          <Route path="/" element={<Welcome onLogin={handleLogin} />} />
          <Route path="/transparency" element={<TransparencyPage onLogin={handleLogin} />} />
        </Routes>
        {showAgeVerification && (
          <AgeVerification 
            onAgeVerified={handleAgeVerified}
            onAgeRejected={handleAgeRejected}
          />
        )}
      </>
    );
  }

  return <AuthenticatedApp />;
}

// Component for authenticated routes
function AuthenticatedApp() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  // Debug logging for AuthenticatedApp
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('AuthenticatedApp: Component mounted/updated', {
        user: !!user,
        currentPath: window.location.pathname
      });
    }
  }, [user]);

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  return (
    <div className="app-container">
      <header className="app-header">
        <h1>Homeschool Activity Generator</h1>
        <div className="user-info">
          <span>Hello, {user.username || user.email || 'User'}</span>
          <button onClick={handleSignOut} className="sign-out-btn">
            Sign Out
          </button>
        </div>
      </header>
      
      <nav className="app-navigation">
        <NavLink 
          to="/generate"
          className={({ isActive }) => `nav-btn ${isActive ? 'active' : ''}`}
        >
          Generate Activity
        </NavLink>
        <NavLink 
          to="/history"
          className={({ isActive }) => `nav-btn ${isActive ? 'active' : ''}`}
        >
          Activity History
        </NavLink>
        <NavLink 
          to="/feedback"
          className={({ isActive }) => `nav-btn ${isActive ? 'active' : ''}`}
        >
          Feedback
        </NavLink>
        <NavLink 
          to="/chat"
          className={({ isActive }) => `nav-btn ${isActive ? 'active' : ''}`}
        >
          Worksheet Assistant
        </NavLink>
        {isAdmin && (
          <NavLink
            to="/admin"
            className={({ isActive }) => `nav-btn nav-btn-admin ${isActive ? 'active' : ''}`}
          >
            ⚙ Admin
          </NavLink>
        )}
      </nav>
      
      <main className="app-content">
        <Routes>
          <Route path="/" element={<HWHistory />} />
          <Route path="/generate" element={<GenerateActivityForm />} />
          <Route path="/history" element={<HWHistory />} />
          <Route path="/feedback" element={<Feedback />} />
          <Route path="/chat" element={<Chat />} />
          {isAdmin && <Route path="/admin" element={<AdminDashboard />} />}
        </Routes>
      </main>
    </div>
  );
}

export default App;